import { useState } from "react";
import Layout from "@/components/layout/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Calculator, TrendingDown, Leaf, PiggyBank } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const SavingsCalculator = () => {
  useSEO({
    title: "Calculadora de ahorro patinete vs coche",
    description: "Calcula cuánto puedes ahorrar al usar un patinete eléctrico frente al coche o el transporte público. Incluye ahorro económico anual y reducción de CO₂.",
    canonical: "/calculadora-ahorro",
  });
  const [newPrice, setNewPrice] = useState(800);
  const [usedPrice, setUsedPrice] = useState(450);
  const [kmPerDay, setKmPerDay] = useState(10);
  const [daysPerWeek, setDaysPerWeek] = useState(5);

  const savings = newPrice - usedPrice;
  const savingsPercent = newPrice > 0 ? ((savings / newPrice) * 100).toFixed(0) : "0";

  // Cost comparison with other transport
  const kmPerYear = kmPerDay * daysPerWeek * 52;
  const electricityCostPerKm = 0.003; // ~0.3 cents per km for e-scooter
  const scooterCostPerYear = kmPerYear * electricityCostPerKm;
  const carCostPerKm = 0.12; // gas + maintenance per km
  const carCostPerYear = kmPerYear * carCostPerKm;
  const publicTransportMonthly = 50;
  const publicTransportPerYear = publicTransportMonthly * 12;

  const carSavings = carCostPerYear - scooterCostPerYear;
  const busSavings = publicTransportPerYear - scooterCostPerYear;

  // CO2 savings
  const co2CarPerKm = 0.12; // kg CO2 per km
  const co2Saved = kmPerYear * co2CarPerKm;

  return (
    <Layout>
      <section className="py-10">
        <div className="container max-w-4xl">
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <Calculator className="h-7 w-7" /> Calculadora de ahorro
          </h1>
          <p className="mt-2 text-muted-foreground">Descubre cuánto puedes ahorrar con un patinete eléctrico</p>

          <div className="grid gap-6 mt-8 md:grid-cols-2">
            {/* Input card */}
            <Card>
              <CardHeader><CardTitle className="font-display text-lg">Datos del patinete</CardTitle></CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <Label>Precio nuevo (€)</Label>
                  <Input type="number" value={newPrice} onChange={(e) => setNewPrice(Number(e.target.value))} />
                </div>
                <div>
                  <Label>Precio segunda mano (€)</Label>
                  <Input type="number" value={usedPrice} onChange={(e) => setUsedPrice(Number(e.target.value))} />
                </div>
                <div>
                  <Label>Km diarios: {kmPerDay} km</Label>
                  <Slider min={1} max={50} step={1} value={[kmPerDay]} onValueChange={([v]) => setKmPerDay(v)} />
                </div>
                <div>
                  <Label>Días por semana: {daysPerWeek}</Label>
                  <Slider min={1} max={7} step={1} value={[daysPerWeek]} onValueChange={([v]) => setDaysPerWeek(v)} />
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            <div className="space-y-4">
              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="p-6 text-center">
                  <TrendingDown className="h-8 w-8 mx-auto text-primary" />
                  <p className="mt-2 text-sm text-muted-foreground">Ahorro comprando de segunda mano</p>
                  <p className="mt-1 font-display text-3xl font-bold text-primary">{savings.toLocaleString("es-ES")} €</p>
                  <p className="text-sm text-muted-foreground">({savingsPercent}% menos que nuevo)</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <PiggyBank className="h-6 w-6 text-secondary mb-2" />
                  <h3 className="font-display font-semibold">Ahorro anual vs. otros transportes</h3>
                  <p className="text-xs text-muted-foreground mb-3">Basado en {kmPerYear.toLocaleString("es-ES")} km/año</p>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">vs. Coche</span>
                      <span className="font-display font-bold text-primary">{carSavings.toLocaleString("es-ES", { maximumFractionDigits: 0 })} €/año</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">vs. Transporte público</span>
                      <span className="font-display font-bold text-primary">{busSavings.toLocaleString("es-ES", { maximumFractionDigits: 0 })} €/año</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Coste eléctrico anual</span>
                      <span className="font-display font-bold">{scooterCostPerYear.toLocaleString("es-ES", { maximumFractionDigits: 2 })} €</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-secondary/30 bg-secondary/5">
                <CardContent className="p-6 text-center">
                  <Leaf className="h-8 w-8 mx-auto text-secondary" />
                  <p className="mt-2 text-sm text-muted-foreground">CO₂ ahorrado vs. coche al año</p>
                  <p className="mt-1 font-display text-2xl font-bold text-secondary">{co2Saved.toLocaleString("es-ES", { maximumFractionDigits: 0 })} kg</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SavingsCalculator;
